import { StorageError } from '../../app/errors';
import { ARTIFACT_PHASE } from '../../types';
import type { CoachCardContext, CoachCardRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `coachCards` — the adjustments-workflow cards (B spec §4.1, scenarios 22–32).
 *
 * **≤1 card per (match, context) is the unique index `&[matchId+context]`'s to enforce** —
 * declared in migration `v004`, at the DB grain. A second row for the same break is a
 * `ConstraintError` inside the transaction, never a handler bug that two screens disagree
 * about. `adjustments` is a JSON column read whole (the `setPieces.markers` shape).
 *
 * Match-scoped: INV-8 cascade + archive entity. The B-3 draft/finalize/discard command
 * writers land beside these in their own unit; B-1 ships what the import, the cascade, and
 * the reads need.
 */

/**
 * Writes one imported card. `add`, not `put` — BAK-2 writes into a profile the same
 * transaction has just emptied. The unique `&[matchId+context]` still applies: two archived
 * cards for one break abort the import, the right outcome for a file this app could not have
 * produced.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id or a duplicate
 *   (match, context) pair, or on a write failure.
 */
export async function insertCoachCard(row: CoachCardRow): Promise<void> {
  requireTransaction('insertCoachCard');
  try {
    await db().coachCards.add(row);
  } catch (cause) {
    throw new StorageError('Could not import the coach card.', {
      cause,
      context: { store: TABLE.coachCards, id: row.id, matchId: row.matchId, cardContext: row.context },
    });
  }
}

/**
 * A match's cards (at most two — one per context), halftime first, for the match record's
 * paged card view.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listForMatch(matchId: string): Promise<CoachCardRow[]> {
  try {
    const rows = await db().coachCards.where('matchId').equals(matchId).toArray();
    // Spelled through `ARTIFACT_PHASE` so the `ses-1` bare-state-literal rule keeps its strength.
    return rows.sort((a, b) =>
      a.context === b.context ? (a.id < b.id ? -1 : 1) : a.context === ARTIFACT_PHASE.halftime ? -1 : 1,
    );
  } catch (cause) {
    throw new StorageError('Could not read the match’s coach cards.', {
      cause,
      context: { store: TABLE.coachCards, matchId },
    });
  }
}

/**
 * B-5 — the Needs-finishing join (`app/homeB.ts`): which matches hold a draft card. A table
 * scan by design: `coachCards` has no `status` index (§4.1 declares `matchId` and the unique
 * `&[matchId+context]` only) and holds at most two rows per match, so an index for this one
 * read would be schema growth in service of nothing. Not on any capture path — Home is read
 * out-of-match.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listDraftCoachCards(): Promise<CoachCardRow[]> {
  try {
    return await db().coachCards.filter((row) => row.status === 'draft').toArray();
  } catch (cause) {
    throw new StorageError('Could not read the draft coach cards.', {
      cause,
      context: { store: TABLE.coachCards },
    });
  }
}

/** B-3 — the one-row read the bus's finalize/discard snapshots use. @throws {StorageError} on a read failure. */
export async function getCoachCard(id: string): Promise<CoachCardRow | undefined> {
  try {
    return await db().coachCards.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the coach card.', {
      cause,
      context: { store: TABLE.coachCards, id },
    });
  }
}

/**
 * B-3 — the card holding one (match, context), read via the unique `&[matchId+context]` index:
 * §2.5's *create-or-reopen* question, answered from committed state inside the transaction.
 *
 * @throws {StorageError} on a read failure.
 */
export async function getForMatchContext(
  matchId: string,
  context: CoachCardContext,
): Promise<CoachCardRow | undefined> {
  try {
    return await db().coachCards.where('[matchId+context]').equals([matchId, context]).first();
  } catch (cause) {
    throw new StorageError('Could not read the coach card for this break.', {
      cause,
      context: { store: TABLE.coachCards, matchId, cardContext: context },
    });
  }
}

/**
 * B-3 — the `upsertCoachCardDraft` op's writer: the whole row, as validated and stamped by the
 * handler (`createdAt` preserved, `updatedAt` from the bus's clock — the stamping lives in the
 * pure layer; this is the mechanical half). `put`, because the same draft is written at every
 * structural act of the workflow (§2.5's interaction-grain commits, the `upsertSetPiece` shape).
 * The unique `&[matchId+context]` index still applies: a second card for a held break aborts
 * the transaction, the DB-grain backstop behind the handler's renderable refusal.
 *
 * @throws {StorageError} outside a transaction, on a duplicate (match, context) pair, or on a
 *   write failure.
 */
export async function upsertCoachCardDraft(row: CoachCardRow): Promise<void> {
  requireTransaction('upsertCoachCardDraft');
  try {
    await db().coachCards.put(row);
  } catch (cause) {
    throw new StorageError('Could not save the coach card draft.', {
      cause,
      context: { store: TABLE.coachCards, id: row.id, matchId: row.matchId, cardContext: row.context },
    });
  }
}

/**
 * B-3 — the `finalizeCoachCard` op's writer: `status` to `'final'`, `finalizedAt` and
 * `updatedAt` stamped, **nothing else touched** — the storage-layer mirror of the op's shape
 * (the `updateAnnotation` discipline): what is frozen is exactly what the last draft commit
 * stored, and a finalize structurally cannot rewrite an adjustment on its way out.
 *
 * @throws {StorageError} outside a transaction, if the card is missing, or on a write failure.
 */
export async function finalizeCoachCard(id: string, at: number): Promise<void> {
  requireTransaction('finalizeCoachCard');
  const row = await db().coachCards.get(id);
  if (!row) {
    throw new StorageError(`Coach card ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.coachCards, id },
    });
  }
  try {
    await db().coachCards.put({ ...row, status: 'final', finalizedAt: at, updatedAt: at });
  } catch (cause) {
    throw new StorageError('Could not finalize the coach card.', {
      cause,
      context: { store: TABLE.coachCards, id },
    });
  }
}

/**
 * B-3 — the `deleteCoachCardDraft` op's writer. Draft-ness (and this-match ownership) is the
 * handler's question, answered as `ERR_CARD_FINALIZED` / `ERR_CARD_NOT_FOUND` from the
 * in-transaction read; this is the mechanical half.
 *
 * @throws {StorageError} outside a transaction or on a write failure.
 */
export async function deleteCoachCardDraft(id: string): Promise<void> {
  requireTransaction('deleteCoachCardDraft');
  try {
    await db().coachCards.delete(id);
  } catch (cause) {
    throw new StorageError('Could not discard the coach card draft.', {
      cause,
      context: { store: TABLE.coachCards, id },
    });
  }
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteCoachCardsForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteCoachCardsForCascade');
  if (ids.length === 0) return;
  try {
    await db().coachCards.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the coach cards during a cascade.', {
      cause,
      context: { store: TABLE.coachCards, count: ids.length },
    });
  }
}
